<!--
  ToolbarDropdown.vue

  A reusable dropdown component for toolbars. Items should be passed
  in via the default slot and must have a .dropdown-item class.

  Features:
  - Click-to-toggle (not hover)
  - Click-outside and Escape to close
  - Multi-dropdown coordination (opening one closes siblings)
  - Optional search input via `searchable` prop
  - Scroll-constrained content (max 60vh)
  - Collision-aware positioning via @floating-ui/vue (flip/shift, live
    repositioning on scroll/resize via autoUpdate)
  -->
<template>
  <div
    ref="dropdownRef"
    class="toolbar-dropdown dropdown"
    :class="[alignmentClass, { 'is-active': isOpen }]"
  >
    <div class="dropdown-trigger" @click.stop="toggle">
      <slot name="button">
        <button
          class="btn"
          :class="{ 'btn-sm': size === 'small' }"
          :title="label"
          aria-haspopup="true"
        >
          <span class="icon is-small trigger-icon">
            <font-awesome-icon :icon="icon || 'circle-question'" />
          </span>
          <span class="trigger-label">{{ label }}</span>
          <span v-if="count" class="trigger-count">({{ count }})</span>
          <span class="icon is-small trigger-caret">
            <font-awesome-icon icon="chevron-down" style="height: 10px" />
          </span>
        </button>
      </slot>
    </div>
    <!-- Teleport menu to <body> so it escapes overflow:auto/hidden/scroll
         and transformed ancestors. Position + collision avoidance (flip/shift)
         is handled by @floating-ui/vue; see `floatingStyles` below. -->
    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="menuRef"
        class="toolbar-dropdown-menu"
        :class="alignmentClass"
        :style="[floatingStyles, { fontSize: menuFontSize }]"
        role="menu"
      >
        <div
          class="dropdown-content"
          :style="{
            width,
            maxHeight: '60vh',
            overflowY: 'auto',
            overscrollBehavior: 'none',
          }"
        >
          <div v-if="searchable" class="dropdown-search">
            <input
              ref="searchInput"
              v-model="searchQuery"
              class="input is-small"
              type="text"
              placeholder="Search..."
              @click.stop
              @keydown.escape.stop="close"
            />
          </div>
          <hr v-if="searchable" class="dropdown-divider" />
          <slot :search="searchQuery" :close="close"></slot>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onBeforeUnmount, onMounted, provide, ref, watch } from "vue"
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/vue"

interface ParentDropdownApi {
  registerDescendantMenu: (el: HTMLElement) => void
  unregisterDescendantMenu: (el: HTMLElement) => void
}

// String key shared across all ToolbarDropdown instances. A Symbol() declared
// here would be re-created on every setup() call (since `<script setup>`
// compiles into setup()), so child injects would never match the parent's
// provide. A separate <script> block could host a module-shared Symbol, but a
// string is simpler.
const PARENT_DROPDOWN_KEY = "toolbar-dropdown-parent"

interface Props {
  icon?: string
  label?: string
  /** How many are applied (sorts, filters, …), shown beside the label — and
   *  kept when a cramped toolbar hides the label. */
  count?: number
  width?: string
  align?: "left" | "right"
  searchable?: boolean
  size?: "normal" | "small"
}

const props = withDefaults(defineProps<Props>(), {
  icon: "circle-question",
  label: "",
  count: 0,
  width: "180px",
  align: "left",
  searchable: false,
  size: "normal",
})

const isOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)
const menuRef = ref<HTMLElement | null>(null)
const searchInput = ref<HTMLInputElement | null>(null)
const searchQuery = ref("")

const alignmentClass = computed(() => (props.align === "right" ? "is-right" : ""))

// Collision-aware positioning (flip/shift keep the menu on-screen near
// viewport edges; autoUpdate repositions live on scroll/resize instead of
// the menu having to close and reopen).
const { floatingStyles } = useFloating(dropdownRef, menuRef, {
  placement: computed(() => (props.align === "right" ? "bottom-end" : "bottom-start")),
  middleware: [offset(4), flip(), shift({ padding: 8 })],
  whileElementsMounted: autoUpdate,
})

// Descendant ToolbarDropdown menus — used to keep this dropdown open when a
// nested dropdown's (teleported) menu receives clicks. Registration bubbles
// up the ancestor chain via inject so every ancestor's outside-click check
// sees nested teleported menus as "inside." Plain Set (not ref): mutations
// on Sets aren't reactive and the value is only read imperatively.
const descendantMenus = new Set<HTMLElement>()
const parentApi = inject<ParentDropdownApi | null>(PARENT_DROPDOWN_KEY, null)

function registerDescendantMenu(el: HTMLElement) {
  descendantMenus.add(el)
  parentApi?.registerDescendantMenu(el)
}

function unregisterDescendantMenu(el: HTMLElement) {
  descendantMenus.delete(el)
  parentApi?.unregisterDescendantMenu(el)
}

provide<ParentDropdownApi>(PARENT_DROPDOWN_KEY, {
  registerDescendantMenu,
  unregisterDescendantMenu,
})

watch(menuRef, (el, prev) => {
  if (prev) parentApi?.unregisterDescendantMenu(prev)
  if (el) parentApi?.registerDescendantMenu(el)
})

onBeforeUnmount(() => {
  if (menuRef.value) parentApi?.unregisterDescendantMenu(menuRef.value)
})

function pathIncludesDescendant(path: EventTarget[]): boolean {
  for (const el of descendantMenus) {
    if (path.includes(el)) return true
  }
  return false
}

const emit = defineEmits<{ close: []; open: [] }>()

// The menu is teleported out of the toolbar, so it can't inherit the trigger's
// size; read it off a standard .btn trigger so a small button opens a small
// menu. Custom triggers (an icon sized for itself) keep the default.
const menuFontSize = ref("var(--auger-size-6)")

function toggle() {
  if (isOpen.value) {
    close()
    return
  }
  const trigger = dropdownRef.value?.querySelector(".dropdown-trigger > .btn")
  menuFontSize.value = trigger ? getComputedStyle(trigger).fontSize : "var(--auger-size-6)"
  isOpen.value = true
  emit("open")
  // Close any other open toolbar dropdowns
  window.dispatchEvent(new CustomEvent("toolbar-dropdown-open", { detail: dropdownRef.value }))
  if (props.searchable) {
    nextTick(() => searchInput.value?.focus())
  }
}

function close() {
  if (!isOpen.value) return
  isOpen.value = false
  emit("close")
}

function handleClickOutside(event: MouseEvent) {
  if (!isOpen.value || !dropdownRef.value) return
  // composedPath() is safe even if the clicked element was detached from the DOM
  const path = event.composedPath()
  if (path.includes(dropdownRef.value)) return
  // The menu is teleported to <body> — clicks inside it must not close.
  if (menuRef.value && path.includes(menuRef.value)) return
  // Nested ToolbarDropdowns teleport their own menus separately; treat
  // clicks inside any registered descendant menu as inside this one.
  if (pathIncludesDescendant(path)) return
  close()
}

function handleEscape(event: KeyboardEvent) {
  if (event.key === "Escape" && isOpen.value) {
    close()
    event.stopImmediatePropagation()
  }
}

function handleOtherOpen(event: Event) {
  const opener = (event as CustomEvent).detail as HTMLElement | null
  if (!opener || opener === dropdownRef.value) return
  // A descendant dropdown (nested inside this one's teleported menu, or
  // inline in the trigger area) is opening — keep this one open. Without
  // this, opening any nested ToolbarDropdown collapses its parent.
  if (menuRef.value?.contains(opener) || dropdownRef.value?.contains(opener)) return
  close()
}

// Reset search when closing
watch(isOpen, (val) => {
  if (!val) searchQuery.value = ""
})

onMounted(() => {
  document.addEventListener("click", handleClickOutside)
  window.addEventListener("keydown", handleEscape)
  window.addEventListener("toolbar-dropdown-open", handleOtherOpen)
})

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside)
  window.removeEventListener("keydown", handleEscape)
  window.removeEventListener("toolbar-dropdown-open", handleOtherOpen)
})

defineExpose({ close, isOpen })
</script>

<style>
.toolbar-dropdown {
  display: inline-flex;

  .dropdown-trigger {
    cursor: pointer;
  }

  /* Muted, as the pipeline filter chips draw theirs: the label carries the
     meaning, the icon and caret just mark it as a dropdown. */
  .trigger-icon,
  .trigger-caret {
    color: var(--auger-text-weak);
  }
}

/* Teleported menu — rendered as a direct child of <body>, positioned via
   @floating-ui/vue's inline `floatingStyles` (position + transform).
   Selector lives outside .toolbar-dropdown because the menu DOM is no
   longer nested inside the trigger. */
.toolbar-dropdown-menu {
  min-width: 12rem;
  /* Above an expanded block (900), which is on <body> too. */
  z-index: 950;

  .dropdown-content {
    background: var(--auger-bg);
    border-radius: var(--auger-radius);
    padding: 0.5rem 0;
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 0.5em;
    padding: 0.375rem 1rem;
    color: var(--auger-text);
    cursor: pointer;
    font-size: inherit;
    white-space: nowrap;

    &:hover {
      background: var(--auger-bg-muted);
    }

    &.is-active {
      background: var(--auger-primary);
      color: white;
    }
  }

  .dropdown-divider {
    border: none;
    border-top: 1px solid var(--auger-border-light);
    margin: 0.25rem 0;
  }

  .dropdown-heading {
    padding: 0.5rem 1rem 0.25rem;
    font-size: 0.85em;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    color: var(--auger-text-weak);
  }

  .dropdown-search {
    padding: 0.375rem 0.75rem;

    .input {
      width: 100%;
    }
  }
}
</style>
